<template>
  <ResponsiveLayout :progress="progress">
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>
    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p class="kds-typography-title-xlarge">
          {{ $t(titleKey) }}
        </p>
      </div>
      <div class="kds-content-block">
        <SelectionCard
          :title="$t(hasCodeTitleKey)"
          :description="$t(hasCodeDescriptionKey)"
          data-qa="code-question-have-code"
          @click="redirectToCodeInput"
        >
          <template #trailing>
            <ChevronRight />
          </template>
        </SelectionCard>
        <SelectionCard
          :title="$t(noCodeTitleKey)"
          :description="$t(noCodeDescriptionKey)"
          data-qa="code-question-no-code"
          @click="redirectToNoCodeStep"
        >
          <template #trailing>
            <ChevronRight />
          </template>
        </SelectionCard>
      </div>
      <BaseBottomSheet
        :title="$t('eu__code_question__help_title')"
        screen-name="code_question_help_bottom_sheet"
      >
        <template #trigger="{ open }">
          <BaseLink
            :text="$t('eu__code_question__help_trigger')"
            @click="open"
          >
            <template #icon>
              <InfoIcon />
            </template>
          </BaseLink>
        </template>
        <div v-html="$t('eu__code_question__help_content')" />
      </BaseBottomSheet>
    </div>
  </ResponsiveLayout>
</template>

<script setup>
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import BaseBottomSheet from '@shared/components/BaseBottomSheet.vue'
import SelectionCard from '@shared/components/SelectionCard.vue'
import InfoIcon from '@shared/assets/icon/InfoIcon.vue'
import ChevronRight from '@shared/assets/icon/ChevronRight.vue'
import * as apiRequest from '@shared/api-endpoints/index.js'
import BaseLink from '@shared/components/BaseLink.vue'
import Tracker from '@shared/Tracker.js'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import useIsResubscription from '@shared/composables/useIsResubscription.ts'
import { computed } from 'vue'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'

const { progress } = useOnboardingProgress()

// Renewing users arrive here from the doctor outreach primer, asking about the
// *new* code their insurer issues for the follow-up prescription.
const isResubscription = useIsResubscription()

const titleKey = computed(() =>
  isResubscription.value
    ? 'eu__code_question__resubscription__title'
    : 'eu__code_question__title',
)
const hasCodeTitleKey = computed(() =>
  isResubscription.value
    ? 'eu__code_question__has_code__resubscription__title'
    : 'eu__code_question__has_code__title',
)
const hasCodeDescriptionKey = computed(() =>
  isResubscription.value
    ? 'eu__code_question__has_code__resubscription__description'
    : 'eu__code_question__has_code__description',
)
const noCodeTitleKey = computed(() =>
  isResubscription.value
    ? 'eu__code_question__no_code__resubscription__title'
    : 'eu__code_question__no_code__title',
)
const noCodeDescriptionKey = computed(() =>
  isResubscription.value
    ? 'eu__code_question__no_code__resubscription__description'
    : 'eu__code_question__no_code__description',
)

const { navigateNext, navigateBack, canGoBack } = useNavigation(
  ROUTE.CODE_QUESTION,
)

const redirectToCodeInput = () => {
  Tracker.trackButtonSelect('has_code', 'code_question')
  apiRequest.sendSubscriptionActivationEntry({
    type: 'user_has_code',
  })
  navigateNext('hasCode')
}

// Renewing users already hold a prescription — they upload it instead of being
// walked through getting free access.
const redirectToNoCodeStep = () => {
  Tracker.trackButtonSelect('no_code', 'code_question')
  apiRequest.sendSubscriptionActivationEntry({
    type: 'user_has_no_code',
  })
  navigateNext('noCode')
}
</script>
